import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { X, Check, Lock, ShieldCheck, AlertCircle, FileText } from "lucide-react";
import ChapterBlocks from "@/components/ChapterBlocks";

export default function SignDocumentModal({ documentId, application, onClose, onSigned }) {
  const [doc, setDoc] = useState(null);
  const [signedName, setSignedName] = useState(application?.data?.full_legal_name || "");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/documents");
      setDoc((data.documents || []).find((d) => d.id === documentId) || null);
    })();
  }, [documentId]);

  const onScroll = (e) => {
    const el = e.target;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) setScrolledToEnd(true);
  };

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/me/application/sign", {
        document_id: documentId,
        signed_name: signedName.trim(),
        confirm,
      });
      onSigned?.(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const ready = scrolledToEnd && confirm && signedName.trim().length >= 2 && !busy;

  if (!doc) {
    return (
      <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/70 backdrop-blur-sm flex items-center justify-center">
        <div className="text-[var(--bloom-bg)] font-serif italic">Loading document…</div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-[var(--bloom-ink)]/70 backdrop-blur-sm overflow-y-auto" data-testid="sign-modal">
      <div className="min-h-screen flex items-start md:items-center justify-center p-4 md:p-12">
        <div className="bg-[var(--bloom-bg)] max-w-3xl w-full relative bloom-rise shadow-2xl">
          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between px-8 md:px-12 py-5 border-b border-[var(--bloom-border)] bg-[var(--bloom-bg)]">
            <div className="flex items-center gap-3 text-xs text-[var(--bloom-ink-muted)] tracking-[0.15em] uppercase">
              <ShieldCheck size={14} className="text-[var(--bloom-brand)]" />
              <span>Electronic Signature · Legally Binding</span>
            </div>
            <button onClick={onClose} className="bloom-btn bloom-btn-ghost" data-testid="sign-modal-close">
              <X size={16} /> Close
            </button>
          </div>

          {/* Scrollable document */}
          <div onScroll={onScroll} className="max-h-[60vh] overflow-y-auto px-8 md:px-12 py-10 border-b border-[var(--bloom-border)]" data-testid="sign-doc-scroll">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--bloom-ink-muted)] mb-4">
              <FileText size={11} className="inline mr-2" />
              Document to sign
            </div>
            <h1 className="font-serif text-3xl md:text-4xl leading-tight mb-3">{doc.title}</h1>
            <p className="font-serif italic text-base text-[var(--bloom-ink-soft)] mb-8">{doc.description}</p>
            <div className="bloom-divider mb-8">{doc.pages} pages · v1.0</div>

            {doc.blocks && doc.blocks.length > 0 ? (
              <ChapterBlocks blocks={doc.blocks} />
            ) : (
              <div className="bloom-prose whitespace-pre-line">{doc.body}</div>
            )}

            <div className="bloom-divider my-10">✦ end of document ✦</div>
          </div>

          {/* Signing zone */}
          <div className="px-8 md:px-12 py-8 bg-[var(--bloom-surface)]">
            {!scrolledToEnd && (
              <div className="flex items-center gap-2 text-xs italic text-[var(--bloom-ink-muted)] font-serif mb-4" data-testid="sign-scroll-prompt">
                <Lock size={12} /> Please scroll through the full document to enable signing.
              </div>
            )}

            <label className={`flex items-start gap-3 p-4 border border-[var(--bloom-border)] cursor-pointer ${!scrolledToEnd ? "opacity-50" : ""}`} data-testid="sign-confirm-row">
              <input
                type="checkbox"
                className="bloom-check mt-0.5"
                disabled={!scrolledToEnd}
                checked={confirm}
                onChange={(e) => setConfirm(e.target.checked)}
                data-testid="sign-confirm-check"
              />
              <span className="text-sm leading-snug">
                I confirm that I have read and understood the <em>{doc.title}</em>, that I am the person named below,
                and that typing my full legal name constitutes my legally binding electronic signature.
              </span>
            </label>

            <div className="mt-6">
              <label className="bloom-label block mb-2">Sign your full legal name</label>
              <input
                type="text"
                value={signedName}
                onChange={(e) => setSignedName(e.target.value)}
                placeholder="e.g. Eleanor Rose Whitaker"
                className="bloom-input font-serif italic text-2xl"
                disabled={!scrolledToEnd}
                data-testid="sign-name-input"
              />
              <p className="text-[11px] text-[var(--bloom-ink-muted)] mt-2 italic font-serif">
                The date, IP address and device will be recorded with your signature as part of the audit trail.
              </p>
            </div>

            {err && (
              <div className="mt-4 flex items-center gap-2 text-sm text-[var(--bloom-accent)] font-serif italic">
                <AlertCircle size={14} /> {err}
              </div>
            )}

            <div className="mt-6 flex items-center justify-between gap-4">
              <button onClick={onClose} className="bloom-btn bloom-btn-ghost" data-testid="sign-cancel-btn">
                Cancel
              </button>
              <button onClick={submit} disabled={!ready} className="bloom-btn bloom-btn-primary" data-testid="sign-submit-btn">
                <Check size={14} /> {busy ? "Signing…" : "Sign Document"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
